import type { Editor } from "@tiptap/core";

/**
 * Double Return = new manuscript row.
 *
 * First Return always makes a line break in the cell (a new empty paragraph).
 * Second Return, while the caret is still in that trailing empty paragraph
 * (or still at the end of the cell after the first Return), creates a row.
 *
 * A cell that is still a single empty paragraph is never a “second Return” —
 * otherwise the first keypress in an empty cell would insert a row.
 *
 * Clicking does not create a row: ProseMirror may turn a click in empty cell
 * space into a synthetic Enter, and DualColumnScript ignores Enter that arrives
 * right after a pointer event.
 */
export function isTrailingEmptyParagraphAtCaret(editor: Editor): boolean {
  const { $from, empty } = editor.state.selection;
  if (!empty) return false;
  if ($from.parent.type.name !== "paragraph") return false;
  if (editor.state.doc.childCount < 2) return false;
  if ($from.parent.textContent.trim() !== "") return false;
  return $from.after() === editor.state.doc.content.size;
}

/** True when Return at the end of existing text should arm “next Return = new row”. */
export function shouldArmNewRowAfterEnter(editor: Editor): boolean {
  const { $from, empty } = editor.state.selection;
  if (!empty) return false;
  if ($from.parent.type.name !== "paragraph") return false;
  if ($from.after() !== editor.state.doc.content.size) return false;
  if (
    editor.state.doc.childCount === 1 &&
    $from.parent.textContent.trim() === ""
  ) {
    return false;
  }
  return $from.parentOffset >= $from.parent.content.size;
}

export function shouldCreateRowOnEnter(
  editor: Editor,
  armed: boolean,
): boolean {
  if (isTrailingEmptyParagraphAtCaret(editor)) return true;
  if (armed && shouldArmNewRowAfterEnter(editor)) return true;
  return false;
}
